import {Component, ElementRef, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {UploadFilesService} from "@catdetails/services/upload-files.service";
import {NotificationsService, ProjectService} from "@catcore/services";
import {Subscription} from "rxjs/Subscription";
import {isNullOrUndefined} from "util";
import {FormArray, FormBuilder, FormGroup} from "@angular/forms";
import {extractDataFromModel} from "@catcore/utils";
import {Observable} from "rxjs/Observable";
import {FileBlob} from "@catcore/types/base";
import * as FileSaver from "file-saver";

@Component({
  selector: 'catapp-upload-files',
  templateUrl: './upload-files.component.html',
  styleUrls: ['./upload-files.component.scss']
})
export class UploadFilesComponent implements OnInit, OnDestroy {

  private projectSubscription: Subscription;
  private uploadSubscription: Subscription;

  project = null;
  form: FormGroup;

  deletions = [];

  @ViewChild('fileInput')
  private fileInput: ElementRef;

  get uploadFilesFormArray(): FormArray {
    return this.form.get('uploadFiles') as FormArray;
  }

  constructor(private uploadFilesService: UploadFilesService,
              private projectService: ProjectService,
              private notificationService: NotificationsService,
              private formBuilder: FormBuilder) {
    this.form = this.formBuilder.group({
      uploadFiles: this.formBuilder.array([])
    });
  }

  refresh() {
    this.clearForm();

    if (isNullOrUndefined(this.project) || isNullOrUndefined(this.project.id)) {
      return;
    }

    if (this.uploadSubscription) {
      this.uploadSubscription.unsubscribe();
    }
    this.uploadSubscription = this.projectService
      .getUploadFiles(this.project)
      .subscribe((uploadFiles) => {
        this.refreshForm(
          extractDataFromModel(uploadFiles, 'item', '_embedded')
        );
      });
  }

  clearForm() {
    this.deletions = [];
    const formArray = this.form.controls['uploadFiles'] as FormArray;
    while (formArray.length !== 0) {
      formArray.removeAt(0);
    }
  }

  refreshForm(uploadFiles) {
    this.clearForm();
    const formArray = this.form.controls['uploadFiles'] as FormArray;
    uploadFiles.forEach((uploadFile) => {
      const formControl = this.buildFileFormGroup(uploadFile.id,
        uploadFile.filename,
        uploadFile.file,
        uploadFile._links['action:download'].href);
      formArray.push(formControl);
    });
  }

  ngOnInit() {
    this.projectSubscription = this.projectService
      .getCurrentProject()
      .subscribe((project) => {
        this.project = project;
        this.refresh();
      });

    this.uploadFilesService.setActive();
  };

  buildFileFormGroup(id, filename, file, downloadUrl) {
    return this.formBuilder.group({
      id: [id, []],
      filename: [filename, []],
      file: [file, []],
      downloadUrl: [downloadUrl, []]
    });
  }

  removeFile(index, id) {
    const formArray = this.form.controls['uploadFiles'] as FormArray;
    formArray.removeAt(index);

    if (id !== null) {
      this.deletions.push({id: id});
    }
    this.checkDirty();
  }

  /**
   * read the provided file-stream and add as a new upload file
   * @param $event
   */
  addFile($event) {
    this.updateFile($event, (name: string, value: string) => {
      const formArray = this.form.controls['uploadFiles'] as FormArray;
      const formGroup = this.buildFileFormGroup(null, name, value, null);
      formGroup.markAsDirty();
      formArray.push(formGroup);
      this.form.markAsDirty();
      this.fileInput.nativeElement.value = '';
    });
  }

  /**
   * read the provided file-stream and update upload file
   * @param $event
   * @param formGroup
   */
  changeFile($event, formGroup) {
    this.updateFile($event, (name: string, value: string) => {
      formGroup.patchValue({
        filename: name,
        file: value
      });
      formGroup.markAsDirty();
      this.form.markAsDirty();
      $event.srcElement.value = '';
    });
  }

  downloadFile(url, filename) {
    const subscription = this.uploadFilesService.downloadFile(url)
      .finally(() => subscription.unsubscribe())
      .subscribe((fileBlob: FileBlob) => {
        const defaultFileName = `${filename}`;
        FileSaver.saveAs(fileBlob.blob, fileBlob.filename ? fileBlob.filename : defaultFileName);
      });
  }

  private updateFile($event, onFinish: (name: string, value: string) => void) {
    for (let i = 0; i < $event.target.files.length; i++) {
      const fileReader = new FileReader();
      const fileInfo = $event.target.files[i];
      const fileName = fileInfo.name;
      fileReader.onload = (fileEvent: any) => {
        const uploadTarget: any = fileEvent.target;
        const fileContent = `${encodeURI(uploadTarget.result)}`;
        onFinish(fileName, fileContent);
      };
      fileReader.readAsDataURL(fileInfo);
    }
  }

  checkDirty() {
    let dirty = false;
    const formArray = this.form.controls['uploadFiles'] as FormArray;
    for (let i = 0; i < formArray.length; i++) {
      if (formArray.at(i).value.id === null) {
        dirty = true;
      }
    }

    dirty = dirty || this.deletions.length > 0;

    if (dirty) {
      this.form.markAsDirty();
    } else {
      this.form.markAsPristine();
    }
  }

  onSubmit() {
    const requests = [];

    const formArray = this.form.controls['uploadFiles'] as FormArray;
    for (let i = 0; i < formArray.length; i++) {
      if (formArray.at(i).dirty) {
        const uploadFile = formArray.at(i).value;
        if (uploadFile.id === null) {
          requests.push(this.uploadFilesService.add({
            project: this.project._links.self.href,
            ...formArray.at(i).value
          }));
        } else {
          requests.push(this.uploadFilesService.update(formArray.at(i).value));
        }
      }
    }

    this.deletions.forEach((item) => {
      requests.push(this.uploadFilesService.delete(item.id))
    });

    const subscription = Observable.forkJoin(requests)
      .finally(() => {
        subscription.unsubscribe();
      })
      .subscribe((results) => {
        const formArray = this.form.controls['uploadFiles'] as FormArray;
        let existingIds = [];
        for (let i = formArray.length - 1; i >= 0; i--) {
          if (formArray.at(i).value.id === null) {
            formArray.removeAt(i);
          } else {
            existingIds.push(formArray.at(i).value.id);
          }
        }
        results.forEach((uploadFile: any) => {
          if (uploadFile['id'] >= 0 && existingIds.indexOf(uploadFile['id']) < 0) {
            formArray.push(this.buildFileFormGroup(uploadFile.id,
              uploadFile.filename,
              uploadFile.file,
              uploadFile._links['action:download'].href));
          }
        });
        this.deletions = [];
        this.form.markAsPristine();
        this.notificationService.success('Upload files saved', 'The upload files have been saved successfully.');
      })
    ;
  }

  ngOnDestroy() {
    this.projectSubscription.unsubscribe();
    this.uploadSubscription.unsubscribe();
    this.uploadFilesService.setInactive();
  }

}



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_develop@2/app/Resources/Public/src/app/modules/project-details/components/userdata-file/userdata-files/upload-files.component.ts